import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import type { AccountId } from './account'

/** An exposed account the active company may connect (`GET /api/ad-accounts/available`). */
export interface AvailableAccount {
  /** The exposed row's id: what `connectAccount` sends back. Never a Meta identifier. */
  id: string
  name: string
  currency: string
  timezone: string | null
  portfolioName: string | null
}

export type AvailableResult =
  | { status: 'ok'; accounts: AvailableAccount[] }
  | { status: 'unavailable' }

/**
 * How a mutation ended. `refused` carries the server's code; `unreachable` covers a
 * request whose outcome is unknown, including one that committed and lost its reply. The
 * panel reconciles after every one of them, so none needs to be trusted about what
 * actually happened.
 */
export type MutationResult =
  | { status: 'ok' }
  | { status: 'refused'; code: 'not_available' | 'not_removable' | 'not_found' | 'other' }
  | { status: 'unreachable' }

/**
 * One session-scoped request, guarded the way `fetchCompanies` is: the generation it was
 * issued under is checked when the response arrives, a 401 is reported as a sign-out, and
 * the request is bound to `authSignal()`.
 */
async function send(url: string, init: RequestInit, fetchImpl: typeof fetch): Promise<Response | null> {
  const generation = authGeneration()
  let response: Response
  try {
    response = await fetchImpl(url, { credentials: 'same-origin', signal: authSignal(), ...init })
  } catch {
    return null
  }
  if (generation !== authGeneration()) return null
  if (response.status === 401) {
    reportSessionExpired()
    return null
  }
  return response
}

function availableOf(value: unknown): AvailableAccount | null {
  if (typeof value !== 'object' || value === null) return null
  const { id, name, currency, timezone, portfolioName } = value as Record<string, unknown>
  if (typeof id !== 'string' || id === '') return null
  if (typeof name !== 'string' || typeof currency !== 'string') return null
  if (timezone !== null && typeof timezone !== 'string') return null
  if (portfolioName !== null && typeof portfolioName !== 'string') return null
  return { id, name, currency, timezone, portfolioName }
}

/**
 * The accounts the company may connect. A `404` from a backend that predates the route
 * reads as an empty list, so the panel renders exactly what it did before GRO-41.
 */
export async function fetchAvailableAccounts(companyId: string, fetchImpl: typeof fetch = fetch): Promise<AvailableResult> {
  const response = await send(`/api/ad-accounts/available?company=${encodeURIComponent(companyId)}`, {}, fetchImpl)
  if (response === null) return { status: 'unavailable' }
  if (response.status === 404) return { status: 'ok', accounts: [] }
  if (!response.ok) return { status: 'unavailable' }
  try {
    const body = (await response.json()) as { accounts?: unknown }
    if (!Array.isArray(body.accounts)) return { status: 'unavailable' }
    const accounts: AvailableAccount[] = []
    for (const entry of body.accounts) {
      const account = availableOf(entry)
      if (account === null) return { status: 'unavailable' }
      accounts.push(account)
    }
    return { status: 'ok', accounts }
  } catch {
    return { status: 'unavailable' }
  }
}

async function mutation(response: Response | null): Promise<MutationResult> {
  if (response === null) return { status: 'unreachable' }
  if (response.ok) return { status: 'ok' }
  if (response.status === 404) return { status: 'refused', code: 'not_found' }
  if (response.status === 409) {
    try {
      const body = (await response.json()) as { error?: unknown }
      if (body.error === 'not_available' || body.error === 'not_removable') {
        return { status: 'refused', code: body.error }
      }
    } catch {
      // A 409 whose body is unreadable is still a refusal.
    }
    return { status: 'refused', code: 'other' }
  }
  return { status: 'unreachable' }
}

/** `POST /api/ad-accounts`: connect an exposed account to the company. */
export async function connectAccount(companyId: string, exposedId: string, fetchImpl: typeof fetch = fetch): Promise<MutationResult> {
  return mutation(await send(
    `/api/ad-accounts?company=${encodeURIComponent(companyId)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ exposedId }) },
    fetchImpl,
  ))
}

/** `DELETE /api/ad-accounts/{id}`: remove the company's demo account and its figures. */
export async function removeAccount(companyId: string, accountId: AccountId, fetchImpl: typeof fetch = fetch): Promise<MutationResult> {
  return mutation(await send(
    `/api/ad-accounts/${encodeURIComponent(accountId)}?company=${encodeURIComponent(companyId)}`,
    { method: 'DELETE' },
    fetchImpl,
  ))
}
